import React, { useCallback, useState } from 'react';

import { Backdrop, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import _ from 'lodash';
import { useGetSavedNotesQuery } from 'src/common/apis/CommonApi';
import PaginationControl from 'src/common/components/PaginationControl';
import { useAppSelector } from 'src/common/hooks/reduxHooks';
import { SaveSheet, SheetStatus } from 'src/common/types/SaveSheet';
import SavedSheetCell from 'src/florence/components/SavedSheetsCell/SavedSheetCell';

import { ReactComponent as NoResultsIcon } from '../../../../common/assets/icons/no_results_icon.svg';

import NoteDeletePopup from './NoteDeletePopup';
import NoteRenamePopup from './NoteRenamePopup';
import NoteSearchTermFilter from './NoteSearchTermFilter';
import NoteStatusFilter from './NoteStatusFilter';

type Props = {
  clinicNotes: boolean;
};

const MyNotes: React.FC<Props> = ({ clinicNotes }) => {
  const { user } = useAppSelector((state) => state.model);

  // cache
  const searchKey = clinicNotes ? 'ClinicNotesSearchTerm' : 'OpNotesSearchTerm';
  const pageKey = clinicNotes ? 'ClinicNotesPage' : 'OpNotesPage';
  const statusKey = clinicNotes ? 'ClinicNotesStatus' : 'OpNotesStatus';

  const [page, setPage] = useState<number>(
    localStorage.getItem(pageKey)
      ? parseInt(localStorage.getItem(pageKey) ?? '1')
      : 1
  );
  const [statusFilter, setStatusFilter] = useState<SheetStatus | undefined>(
    (localStorage.getItem(statusKey) as SheetStatus) ?? undefined
  );
  const [searchTerm, setSearchTerm] = useState<string>(
    localStorage.getItem(searchKey) ?? ''
  );

  // misc
  const [deleteContainerOpen, setDeleteContainerOpen] = useState(false);
  const [renameContainerOpen, setRenameContainerOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState<SaveSheet | null>(null);

  // request fetching the notes
  const { data, isLoading, isFetching } = useGetSavedNotesQuery(
    {
      limit: 10, // items per page
      page: page,
      status: statusFilter,
      searchQuery: searchTerm,
      clinicNotes: clinicNotes, // wether we need Clinic Notes or Op Notes
    },
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: 60,
    }
  );

  // scroll to the top of the notes section when page is changed
  const scrollIntoView = useCallback(() => {
    const element = document.getElementById(
      clinicNotes ? 'my-clinic-notes' : 'my-op-notes'
    );
    const rect = element?.getBoundingClientRect();
    if (rect) {
      window.scrollTo({
        top: rect.top + window.scrollY - 108,
        behavior: 'smooth',
      });
    }
  }, [clinicNotes]);

  const changePage = useCallback(
    (page: number) => {
      localStorage.setItem(pageKey, page.toString());
      setPage(page);
      scrollIntoView();
    },
    [pageKey, setPage, scrollIntoView]
  );

  const changeSearchTerm = useCallback(
    (searchTerm: string) => {
      localStorage.setItem(searchKey, searchTerm);
      setSearchTerm(searchTerm);
      if (page !== 1) changePage(1);
    },
    [searchKey, setSearchTerm, page, changePage]
  );

  const changeStatusFilter = useCallback(
    (status: SheetStatus | undefined) => {
      localStorage.setItem(statusKey, status ?? 'undefined');

      status
        ? localStorage.setItem(statusKey, status)
        : localStorage.removeItem(statusKey);
      setStatusFilter(status);
    },
    [statusKey]
  );

  const openDeleteSheetContainer = (savedSheet: SaveSheet) => {
    setDeleteContainerOpen(true);
    setSelectedSheet(savedSheet);
  };

  const openRenameSheetContainer = (savedSheet: SaveSheet) => {
    setRenameContainerOpen(true);
    setSelectedSheet(savedSheet);
  };

  if (!user?.activeSubscriptionType && !user?.organizationId) {
    return (
      <Typography color='text.secondary' textAlign='center' mt='56px'>
        To view your {clinicNotes ? 'Clinic' : 'Op'} Notes you must have an
        active subscription or be a member of an organization.
      </Typography>
    );
  }

  const results =
    user.activeSubscriptionType === 'mobile' ||
    user.activeSubscriptionType === 'trial'
      ? _.slice(data?.savedSheets, 0, 5)
      : data?.savedSheets;

  return (
    <Box id={clinicNotes ? 'my-clinic-notes' : 'my-op-notes'}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          width: '100%',
        }}
      >
        <Typography
          fontSize='18px'
          fontWeight={700}
          color={
            clinicNotes ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 33, 69, 0.85)'
          }
        >
          My {clinicNotes ? 'Clinic' : 'Op'} Notes
        </Typography>
        <Box
          sx={{
            marginLeft: { xs: '0px', sm: 'auto' },
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <NoteSearchTermFilter
            clinicNotes={clinicNotes}
            onChange={changeSearchTerm}
            value={searchTerm}
          />
          <NoteStatusFilter
            onChange={changeStatusFilter}
            value={statusFilter}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          marginTop: '24px',
          minHeight: '120px',
        }}
      >
        {(isLoading || isFetching) && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: '42px',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              zIndex: 99,
              borderRadius: '16px',
            }}
          >
            <CircularProgress color='secondary' size='32px' />
          </Box>
        )}

        {!isLoading &&
          !isFetching &&
          results?.length === 0 &&
          searchTerm === '' &&
          page === 1 &&
          !statusFilter && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '56px',
              }}
            >
              <NoResultsIcon />
              <Typography color='text.secondary' textAlign='center' mt='16px'>
                No {clinicNotes ? 'Clinic' : 'Op'} Notes were saved yet.
              </Typography>
            </Box>
          )}

        {!isLoading &&
          !isFetching &&
          results?.length === 0 &&
          (searchTerm !== '' || page !== 1 || statusFilter) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '56px',
              }}
            >
              <NoResultsIcon />
              <Typography color='text.secondary' textAlign='center' mt='16px'>
                No {clinicNotes ? 'Clinic' : 'Op'} Notes were found.
              </Typography>
            </Box>
          )}

        {results?.map((result) => (
          <SavedSheetCell
            type='florence'
            key={result.sheetId}
            savedSheet={result}
            deleteSavedSheet={openDeleteSheetContainer}
            renameSavedSheet={openRenameSheetContainer}
          />
        ))}
        {results?.length !== 0 && !isLoading && (
          <PaginationControl
            sx={{ alignSelf: 'center' }}
            count={Math.ceil((data?.total ?? 0) / 10)}
            onChange={(e, p) => changePage(p)}
            page={page}
          />
        )}
      </Box>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={deleteContainerOpen || renameContainerOpen}
      >
        {selectedSheet && renameContainerOpen && (
          <NoteRenamePopup
            note={selectedSheet}
            onClose={() => setRenameContainerOpen(false)}
          />
        )}
        {selectedSheet && deleteContainerOpen && (
          <NoteDeletePopup
            note={selectedSheet}
            onClose={() => setDeleteContainerOpen(false)}
          />
        )}
      </Backdrop>
    </Box>
  );
};

export default MyNotes;
